import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { DownloadIcon, FileTextIcon, CheckSquareIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { downloadChecklist } from '../utils/downloadChecklist';
import { useLanguage } from '../contexts/LanguageContext';
interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
  link?: string;
  onDownload?: () => void;
}
function ResourceCard({
  icon,
  title,
  description,
  delay,
  link,
  onDownload
}: ResourceCardProps) {
  const {
    t
  } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay
  }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-137">
      <div className="flex justify-center mb-6" data-id="element-138">
        <div className="w-16 h-16 bg-[#1a365d] rounded-full flex items-center justify-center text-white" data-id="element-139">
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-bold text-[#1a365d] mb-4 text-center" data-id="element-140">
        {title}
      </h3>
      <p className="text-gray-700 mb-6 leading-relaxed text-center" data-id="element-141">
        {description}
      </p>
      {link ? <Link to={link} className="w-full bg-[#d97706] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#b45309] transition-colors flex items-center justify-center gap-2" data-id="element-142">
          <ArrowRightIcon className="w-5 h-5" data-id="element-143" />
          {t('Ver Guía', 'View Guide')}
        </Link> : <button onClick={onDownload} className="w-full bg-[#d97706] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#b45309] transition-colors flex items-center justify-center gap-2" data-id="element-144">
          <DownloadIcon className="w-5 h-5" data-id="element-145" />
          {t('Descargar', 'Download')}
        </button>}
    </motion.div>;
}
export function ResourcesSection() {
  const {
    t
  } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const resources = [{
    icon: <FileTextIcon className="w-8 h-8" data-id="element-146" />,
    title: t('Guía Básica de Medicare', 'Basic Medicare Guide'),
    description: t('Todo lo que necesitas saber sobre las partes de Medicare y cómo elegir el plan adecuado.', 'Everything you need to know about the parts of Medicare and how to choose the right plan.'),
    link: '/guia-medicare'
  }, {
    icon: <CheckSquareIcon className="w-8 h-8" data-id="element-147" />,
    title: t('Checklist de Cobertura Familiar', 'Family Coverage Checklist'),
    description: t('Asegúrate de que tu familia tenga la protección completa con esta lista de verificación.', 'Ensure your family has complete protection with this checklist.'),
    onDownload: downloadChecklist
  }];
  return <section id="recursos" className="py-20 bg-white" data-id="element-148">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-149">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-150">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-4" data-id="element-151">
            {t('Recursos Útiles', 'Useful Resources')}
          </h2>
          <div className="w-24 h-1 bg-[#d97706] mx-auto mb-6" data-id="element-152"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-153">
            {t('Herramientas y guías para ayudarte a tomar mejores decisiones', 'Tools and guides to help you make better decisions')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-154">
          {resources.map((resource, index) => <ResourceCard key={index} {...resource} delay={0.2 + index * 0.1} data-id="element-155" />)}
        </div>
      </div>
    </section>;
}